import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";

export type HabitCategory =
  | "hydration"
  | "sleep"
  | "stress"
  | "energy"
  | "movement"
  | "nutrition"
  | "recovery"
  | "mindfulness"
  | "custom";

export type TrackingType = "boolean" | "count" | "duration" | "quality_score";

export interface Habit {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  category: HabitCategory;
  tracking_type: TrackingType;
  unit: string | null;
  default_target: number | null;
  is_system_template: boolean | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export type ProgressionType = "weekly" | "biweekly" | "monthly";

export interface HabitClient {
  id: string;
  habit_id: string;
  client_id: string;
  organization_id: string;
  frequency: string;
  frequency_days: number[] | null;
  target_value: number | null;
  start_date: string;
  end_date: string | null;
  is_active: boolean | null;
  notes: string | null;
  // Progression fields
  progression_enabled: boolean | null;
  progression_type: ProgressionType | null;
  progression_increment: number | null;
  progression_max_target: number | null;
  initial_target: number | null;
  created_at: string | null;
  updated_at: string | null;
  habit?: Habit;
  client?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

export interface HabitLog {
  id: string;
  habit_client_id: string;
  client_id: string;
  log_date: string;
  completed: boolean | null;
  value: number | null;
  notes: string | null;
  logged_at: string | null;
  updated_at: string | null;
}

export interface CreateHabitInput {
  name: string;
  description?: string;
  icon?: string;
  category: HabitCategory;
  tracking_type: TrackingType;
  unit?: string;
  default_target?: number;
}

export interface AssignHabitInput {
  habit_id: string;
  client_id: string;
  frequency?: string;
  frequency_days?: number[];
  target_value?: number;
  start_date?: string;
  end_date?: string;
  notes?: string;
  // Progression fields
  progression_enabled?: boolean;
  progression_type?: ProgressionType;
  progression_increment?: number;
  progression_max_target?: number;
  initial_target?: number;
}

export function useHabits() {
  const { organization } = useOrganization();
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchHabits = useCallback(async () => {
    if (!organization?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("habits")
        .select("*")
        .eq("organization_id", organization.id)
        .order("category")
        .order("name");

      if (error) throw error;
      setHabits(data || []);
    } catch (error) {
      console.error("Error fetching habits:", error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    fetchHabits();
  }, [fetchHabits]);

  const createHabit = async (input: CreateHabitInput): Promise<Habit | null> => {
    if (!organization?.id) return null;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("habits")
        .insert({
          organization_id: organization.id,
          created_by: user.id,
          name: input.name,
          description: input.description || null,
          icon: input.icon || "circle-check",
          category: input.category,
          tracking_type: input.tracking_type,
          unit: input.unit || null,
          default_target: input.default_target || null,
          is_system_template: false,
        })
        .select()
        .single();

      if (error) throw error;

      // Refresh habits list
      await fetchHabits();
      return data;
    } catch (error) {
      console.error("Error creating habit:", error);
      throw error;
    }
  };

  const updateHabit = async (habitId: string, input: Partial<CreateHabitInput>): Promise<Habit | null> => {
    try {
      const { data, error } = await supabase
        .from("habits")
        .update({
          name: input.name,
          description: input.description,
          icon: input.icon,
          category: input.category,
          tracking_type: input.tracking_type,
          unit: input.unit,
          default_target: input.default_target,
          updated_at: new Date().toISOString(),
        })
        .eq("id", habitId)
        .select()
        .single();

      if (error) throw error;

      await fetchHabits();
      return data;
    } catch (error) {
      console.error("Error updating habit:", error);
      throw error;
    }
  };

  const deleteHabit = async (habitId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("habits")
        .delete()
        .eq("id", habitId);

      if (error) throw error;

      await fetchHabits();
      return true;
    } catch (error) {
      console.error("Error deleting habit:", error);
      throw error;
    }
  };

  return {
    habits,
    loading,
    refetch: fetchHabits,
    createHabit,
    updateHabit,
    deleteHabit,
  };
}

export function useClientHabits(clientId: string | undefined) {
  const { organization } = useOrganization();
  const [assignments, setAssignments] = useState<HabitClient[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAssignments = useCallback(async () => {
    if (!clientId || !organization?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("habit_clients")
        .select(`
          *,
          habit:habits (*)
        `)
        .eq("client_id", clientId)
        .eq("organization_id", organization.id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform data to match interface
      const transformed = (data || []).map((item: any) => ({
        ...item,
        habit: item.habit,
      }));

      setAssignments(transformed);
    } catch (error) {
      console.error("Error fetching client habits:", error);
    } finally {
      setLoading(false);
    }
  }, [clientId, organization?.id]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const assignHabit = async (input: AssignHabitInput): Promise<HabitClient | null> => {
    if (!organization?.id) return null;

    try {
      const { data, error } = await supabase
        .from("habit_clients")
        .insert({
          habit_id: input.habit_id,
          client_id: input.client_id,
          organization_id: organization.id,
          frequency: input.frequency || "daily",
          frequency_days: input.frequency_days || null,
          target_value: input.target_value || null,
          start_date: input.start_date || new Date().toISOString().split("T")[0],
          end_date: input.end_date || null,
          notes: input.notes || null,
          is_active: true,
          // Progression fields
          progression_enabled: input.progression_enabled || false,
          progression_type: input.progression_type || null,
          progression_increment: input.progression_increment || null,
          progression_max_target: input.progression_max_target || null,
          initial_target: input.initial_target || null,
        })
        .select(`
          *,
          habit:habits (*)
        `)
        .single();

      if (error) throw error;

      await fetchAssignments();
      return data;
    } catch (error) {
      console.error("Error assigning habit:", error);
      throw error;
    }
  };

  const unassignHabit = async (assignmentId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("habit_clients")
        .update({ is_active: false })
        .eq("id", assignmentId);

      if (error) throw error;

      await fetchAssignments();
      return true;
    } catch (error) {
      console.error("Error unassigning habit:", error);
      throw error;
    }
  };

  return {
    assignments,
    loading,
    refetch: fetchAssignments,
    assignHabit,
    unassignHabit,
  };
}

export function useHabitLogs(habitClientId: string | undefined, dateRange?: { start: string; end: string }) {
  const [logs, setLogs] = useState<HabitLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    if (!habitClientId) {
      setLoading(false);
      return;
    }

    try {
      let query = supabase
        .from("habit_logs")
        .select("*")
        .eq("habit_client_id", habitClientId)
        .order("log_date", { ascending: false });

      if (dateRange) {
        query = query
          .gte("log_date", dateRange.start)
          .lte("log_date", dateRange.end);
      }

      const { data, error } = await query;

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error("Error fetching habit logs:", error);
    } finally {
      setLoading(false);
    }
  }, [habitClientId, dateRange?.start, dateRange?.end]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const logHabit = async (
    habitClientId: string,
    clientId: string,
    logDate: string,
    completed: boolean,
    value?: number,
    notes?: string
  ): Promise<HabitLog | null> => {
    try {
      // Upsert to handle re-logging the same day
      const { data, error } = await supabase
        .from("habit_logs")
        .upsert(
          {
            habit_client_id: habitClientId,
            client_id: clientId,
            log_date: logDate,
            completed,
            value: value || null,
            notes: notes || null,
            logged_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "habit_client_id,log_date",
          }
        )
        .select()
        .single();

      if (error) throw error;

      await fetchLogs();
      return data;
    } catch (error) {
      console.error("Error logging habit:", error);
      throw error;
    }
  };

  return {
    logs,
    loading,
    refetch: fetchLogs,
    logHabit,
  };
}

// Category metadata for UI
export const HABIT_CATEGORIES: Record<HabitCategory, { label: string; icon: string; color: string }> = {
  hydration: { label: "Hydration", icon: "droplets", color: "text-info" },
  sleep: { label: "Sleep", icon: "moon", color: "text-chart-4" },
  stress: { label: "Stress", icon: "brain", color: "text-warning" },
  energy: { label: "Energy", icon: "zap", color: "text-warning" },
  movement: { label: "Movement", icon: "footprints", color: "text-success" },
  nutrition: { label: "Nutrition", icon: "utensils", color: "text-success" },
  recovery: { label: "Recovery", icon: "heart-pulse", color: "text-destructive" },
  mindfulness: { label: "Mindfulness", icon: "lotus", color: "text-chart-4" },
  custom: { label: "Custom", icon: "star", color: "text-muted-foreground" },
};

export const TRACKING_TYPES: Record<TrackingType, { label: string; description: string }> = {
  boolean: { label: "Yes/No", description: "Simple checkbox completion" },
  count: { label: "Count", description: "Track a number (e.g., glasses of water)" },
  duration: { label: "Duration", description: "Track time (e.g., minutes of meditation)" },
  quality_score: { label: "Quality Score", description: "Rate on a scale of 1-10" },
};

export const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Every day" },
  { value: "weekdays", label: "Weekdays only" },
  { value: "weekends", label: "Weekends only" },
  { value: "specific_days", label: "Specific days" },
];

export const PROGRESSION_TYPE_OPTIONS: { value: ProgressionType; label: string; description: string }[] = [
  { value: "weekly", label: "Weekly", description: "Increase target every week" },
  { value: "biweekly", label: "Bi-weekly", description: "Increase target every 2 weeks" },
  { value: "monthly", label: "Monthly", description: "Increase target every month" },
];

// Utility function to calculate progression preview
export interface ProgressionPreviewPoint {
  period: number;
  target: number;
  label: string;
}

export function calculateProgressionPreview(
  initialTarget: number,
  increment: number,
  maxTarget: number | undefined,
  progressionType: ProgressionType,
  periods: number = 8
): ProgressionPreviewPoint[] {
  const preview: ProgressionPreviewPoint[] = [];

  for (let i = 0; i < periods; i++) {
    let target = initialTarget + i * increment;

    // Apply max cap if set
    if (maxTarget !== undefined) {
      target = Math.min(target, maxTarget);
    }

    let label = "";
    switch (progressionType) {
      case "weekly":
        label = i === 0 ? "Start" : `Week ${i + 1}`;
        break;
      case "biweekly":
        label = i === 0 ? "Start" : `Week ${i * 2 + 1}`;
        break;
      case "monthly":
        label = i === 0 ? "Start" : `Month ${i + 1}`;
        break;
    }

    preview.push({ period: i, target, label });

    // Stop if we've hit the max
    if (maxTarget !== undefined && target >= maxTarget) {
      break;
    }
  }

  return preview;
}
